import { useEffect, useState } from "react";

interface UseAnnouncementDialogOptions {
  /**
   * Whether to enable the automatic daily popup behavior
   * @default true
   */
  enableAutoShow?: boolean;
  /**
   * How many hours to wait before showing the dialog again
   * @default 24
   */
  intervalHours?: number;
  /**
   * localStorage key for storing the last shown timestamp
   * @default "announcementDialogLastShown"
   */
  storageKey?: string;
}

interface UseAnnouncementDialogReturn {
  /** Whether the dialog is currently open */
  isOpen: boolean;
  /** Function to open the dialog manually */
  open: () => void;
  /** Function to close the dialog */
  close: () => void;
  /** Function to toggle the dialog state */
  toggle: () => void;
}

/**
 * Custom hook to manage the announcement dialog state and automatic display logic
 */
export const useAnnouncementDialog = (
  options: UseAnnouncementDialogOptions = {},
): UseAnnouncementDialogReturn => {
  const {
    enableAutoShow = true,
    intervalHours = 24,
    storageKey = "announcementDialogLastShown",
  } = options;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!enableAutoShow) return;

    // Check if dialog was shown in the last interval
    const lastShownTime = localStorage.getItem(storageKey);
    const currentTime = Date.now();
    const intervalInMs = intervalHours * 60 * 60 * 1000; // Convert hours to milliseconds

    // If lastShownTime doesn't exist or it's been more than the interval
    if (
      !lastShownTime ||
      currentTime - parseInt(lastShownTime) > intervalInMs
    ) {
      console.log("Opening announcement dialog (auto show)");
      setIsOpen(true);
      localStorage.setItem(storageKey, currentTime.toString());
    } else {
      console.log(
        "Announcement dialog last shown date: ",
        new Date(parseInt(lastShownTime)).toLocaleString(),
      );
    }
  }, [enableAutoShow, intervalHours, storageKey]);

  const open = () => setIsOpen(true);

  const close = () => setIsOpen(false);

  const toggle = () => setIsOpen((prev) => !prev);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
};
